// Enum for permission actions
export enum PermissionAction {
    CREATE = 'create',
    READ = 'read',
    UPDATE = 'update',
    DELETE = 'delete',
    MANAGE = 'manage'
}

// Enum for permission subjects/modules
export enum PermissionSubject {
    ALL = 'all',
    LOG = 'log',
    USER = 'user',
    ROLE = 'role',
    INFORMATION = 'information',
    CATEGORY = 'category',
    ARTICLE = 'article',
    IMAGE = 'image',
    PAGE = 'page',
    BLOG_POST = 'blog_post',
    MENU = 'menu',
    FILM_LIST = 'film_list',
    AUTHOR = 'author',
    ROBOT_TXT = 'robot_txt',
    WIDGET = 'widget',
    COMMENT = 'comment',
    ACTOR = 'actor',
    DIRECTOR = 'director',
    KKMOVIE = 'kkmovie',
    KKPHIM_EPISODE = 'kkphim_episode',
    KKPHIM_SYNC = 'kkphim_sync',
    OPHIM_SYNC = 'ophim_sync',
    BANNER = 'banner',
    TEXTLINK = 'textlink',
}

// Interface for permission object
export interface Permission {
    _id: string;
    role: {
        _id: string;
        name: string;
    };
    action: string[];
    subject: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

/**
 * Get user permissions from localStorage
 */
export const getUserPermissions = (): Permission[] => {
    try {
        const permissions = localStorage.getItem('permission');
        return permissions ? JSON.parse(permissions) : [];
    } catch (error) {
        console.error('Error getting user permissions:', error);
        return [];
    }
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (
    subject: PermissionSubject | string,
    action: PermissionAction | string
): boolean => {
    const permissions = getUserPermissions();
    if (!permissions || permissions.length === 0) {
        return false;
    }

    // Check if user is admin (has all permissions)
    const adminPermission = permissions.find(
        (permission) =>
            permission.subject === PermissionSubject.ALL &&
            permission.action.includes(PermissionAction.MANAGE)
    );

    if (adminPermission) {
        return true; // Admin has all permissions
    }

    // Check specific subject and action
    const hasSubjectPermission = permissions.find((permission) => {
        const hasSubject = permission.subject === subject;
        const hasAction = permission.action.includes(action) ||
            permission.action.includes(PermissionAction.MANAGE);

        return hasSubject && hasAction;
    });

    return !!hasSubjectPermission;
};

/**
 * Check if user can create on specific subject
 */
export const canCreate = (subject: PermissionSubject | string): boolean => {
    return hasPermission(subject, PermissionAction.CREATE);
};

/**
 * Check if user can read specific subject
 */
export const canRead = (subject: PermissionSubject | string): boolean => {
    return hasPermission(subject, PermissionAction.READ);
};

/**
 * Check if user can update specific subject
 */
export const canUpdate = (subject: PermissionSubject | string): boolean => {
    return hasPermission(subject, PermissionAction.UPDATE);
};

/**
 * Check if user can delete specific subject
 */
export const canDelete = (subject: PermissionSubject | string): boolean => {
    return hasPermission(subject, PermissionAction.DELETE);
};

/**
 * Check if user can manage (all actions) on specific subject
 */
export const canManage = (subject: PermissionSubject | string): boolean => {
    return hasPermission(subject, PermissionAction.MANAGE);
};

/**
 * Check if user is admin
 */
export const isAdmin = (): boolean => {
    return hasPermission(PermissionSubject.ALL, PermissionAction.MANAGE);
};

/**
 * Get user role name
 */
export const getUserRole = (): string => {
    const permissions = getUserPermissions();
    return permissions?.[0]?.role?.name || 'Unknown';
};

/**
 * Check multiple permissions at once
 */
export const hasAnyPermission = (checks: Array<{ subject: string, action: string }>): boolean => {
    return checks.some(({ subject, action }) => hasPermission(subject, action));
};

/**
 * Check all permissions are granted
 */
export const hasAllPermissions = (checks: Array<{ subject: string, action: string }>): boolean => {
    return checks.every(({ subject, action }) => hasPermission(subject, action));
};
